/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-disable max-lines */

import { DS } from 'js-data';

import analyticsCounterResource from '../analytics/analytics-counter-resource';
import analyticsGroupResource from '../analytics/analytics-group-resource';
import assetResource from '../asset/asset-resource';
import contentTaskResource from '../content/task/content-task-resource';
import exceptionResource from '../exception/exception-resource';
import layerResource from '../layer/layer-resource';
import layoutResource from '../layout/layout-resource';
import mediumResource from '../medium/medium-resource';
import playlistResource from '../playlist/playlist-resource';
import powerTaskResource from '../power/power-task-resource';
import PromiseUtil from '../promise/promise-util';
import sessionResource from '../session/session-resource';
import taskResource from '../task/task-resource';
import timerResource from '../timer/timer-resource';
import trackResource from '../track/track-resource';

/**
 * @import { NonEmptyArray } from '../../util.type'
 * @import { AnalyticsCounter } from '../analytics/analytics-counter'
 * @import { AnalyticsCounterAttrs } from '../analytics/analytics-counter-attrs'
 * @import { AnalyticsGroup } from '../analytics/analytics-group'
 * @import { AnalyticsGroupAttrs, AnalyticsGroupVxAttrs } from '../analytics/analytics-group-attrs'
 * @import { Asset } from '../asset/asset'
 * @import { AssetAttrs } from '../asset/asset-attrs'
 * @import { ContentTask } from '../content/task/content-task'
 * @import { ContentTaskAttrs } from '../content/task/content-task-attrs'
 * @import { DSAdapterOperationConfiguration, DSConfiguration, DSSaveConfiguration, IDSAdapter } from 'js-data'
 * @import { Layer } from '../layer/layer'
 * @import { LayerAttrs } from '../layer/layer-attrs'
 * @import { Layout } from '../layout/layout'
 * @import { LayoutAttrs } from '../layout/layout-attrs'
 * @import { Medium } from '../medium/medium'
 * @import { MediumAttrs } from '../medium/medium-attrs'
 * @import { Playlist } from '../playlist/playlist'
 * @import { PlaylistAttrs } from '../playlist/playlist-attrs'
 * @import { Session } from '../session/session'
 * @import { SessionAttrs } from '../session/session-attrs'
 * @import { Track } from '../track/track'
 * @import { TrackAttrs } from '../track/track-attrs'
 */

// Override některých používaných properties s řádnou dokumentací a typy.
/**
 * @private
 * @typedef {Object} dsAdapterOperationConfiguration
 * @property {'merge' | 'replace'} [onConflict = 'merge']
 * What to do when injecting an item into the store, when an item with the same primary key already exists in the store.
 */

/**
 * @template M
 * @typedef {keyof M} ResourceName
 */

/**
 * Mapa s typy pro perzistentní zdroje.
 * @typedef {{
 *   [analyticsCounterResource.name]: [AnalyticsCounterAttrs, AnalyticsCounter],
 *   [analyticsGroupResource.name]: [AnalyticsGroupAttrs, AnalyticsGroup],
 *   [assetResource.name]: [AssetAttrs, Asset],
 *   [sessionResource.name]: [SessionAttrs, Session],
 * }} PersistentMap
 */

/**
 * Mapa s typy pro transient zdroje.
 * @typedef {{
 *   [contentTaskResource.name]: [ContentTaskAttrs, ContentTask],
 *   [mediumResource.name]: [MediumAttrs, Medium],
 *   [layerResource.name]: [LayerAttrs, Layer],
 *   [layoutResource.name]: [LayoutAttrs, Layout],
 *   [playlistResource.name]: [PlaylistAttrs, Playlist],
 *   [trackResource.name]: [TrackAttrs, Track],
 * }} TransientMap
 */

/**
 * Utility type pro získání typu z Attrs, kde jsou všechny položky
 * kromě 'id' volitelné (tj. undefined) anebo s hodnotou null.
 *
 * Typické použití je pro takové modely/attrs, které se po spuštění
 * aplikace explicitně a jednorázově nemigrují, takže se mohou načíst
 * stará data, která např. některé povinné položky neobsahují nebo
 * "null volitelné" (kvůli problémům s ukládáním undefined hodnot
 * v JSData musí být volitelnost modelována vždy takto) položky mohou
 * nabývat i nechtěného typu undefined.
 *
 * Z toho důvodu je správné tento typ používat spíše pro model a nikoliv
 * attrs!
 * @template {{ id: any }} Attrs
 * @typedef {Pick<Attrs, 'id'> & { [P in Exclude<keyof Attrs, 'id'>]?: Attrs[P] | null }} MaybeAttrs
 */

/**
 * Utility typ pro atributy pro update (bez id a všechno volitelné).
 * @template {{ id: any }} T
 * @typedef {Partial<Omit<T, 'id'>>} UpdateAttrs
 */

/**
 * Typované {@link DSFilterParams}.
 * @see https://www.js-data.io/v2.9/docs/query-syntax
 * @template {{ id: any }} T
 * @typedef {Omit<import('js-data').DSFilterParams, 'orderBy' | 'sort' | 'where'> & {
 *   orderBy?: keyof T | (keyof T)[] | [keyof T, 'ASC' | 'DESC'][];
 *   where?: { [P in keyof T]?:
 *     | T[P]
 *     | Partial<Record<
 *       | '!='
 *       | '!=='
 *       | '<'
 *       | '<='
 *       | '=='
 *       | '==='
 *       | '>'
 *       | '>='
 *     , T[P]>>
 *     | Partial<Record<
 *       | 'in'
 *       | 'notIn'
 *     , NonEmptyArray<T[P]>>>
 *   };
 * }} DSFilterParams
 */

/**
 * @template {{ id: any }} T
 * @typedef {Omit<DSFilterParams<T>, 'where'> & Required<Pick<DSFilterParams<T>, 'where'>>} DSFilterParamsRequiredWhere
 */

// TODO #555: Pro generické metody nejde odvodit stub/spy typ v testech
// stávajícím způsobem jako pro negenerické, takže je pak nutné/lepší používat
// negenericky typované varianty jako `saveAsset` místo save<'asset'>.
// Tak nakonec to jde jinak pomocí
// `sinon.SinonSpiedMember<typeof store.save<'asset'>>`
// místo
// `sinon.SinonSpiedMember<Store['saveAsset']>`,
// tj. k odvození typu potřebujeme instanci Store, aby šlo získat instanci
// generické metody přes tečku a špičaté závorky.
// TODO #471 Doc: Aktualizovat store diagram (Asset#checksum)
/**
 * Datový sklad postavený na knihovně JSData.
 *
 * ![Diagram tříd](./core/store/store.doc.png)
 */
class Store {
  // TODO: Mít opts je sice obecné, ale nikde se nepoužívá.
  /**
   * Vytvoří datový sklad.
   * @param {{ storage?: IDSAdapter }} [dsAdapterMap] Mapa adaptérů pro JSData.
   * @param {import('js-data').DSConfiguration} [opts = {}]
   */
  constructor(dsAdapterMap = {}, opts = {}) {
    /**
     * Nemůže být private kvůli použití ve store.unit.js
     * @deprecated Nevolat napřímo, jen ve výjimečných situacích.
     */
    this.ds = new DS({
      // Simple where může způsobit nechtěné operace např. {limit: 1} má filtrovat, nebo stránkovat?
      allowSimpleWhere: false,
      // Načítání z cache pouze na vyžádání. Může způsobit nepříliš očekávané chování.
      bypassCache: true,
      defaultAdapter: 'storage',
      // Optimalizace výkonu. Naslouchat na změny u datových zdrojů.
      instanceEvents: false,
      // Optimalizace výkonu. Deprecated computed properties. Používat raději getter / setter styl.
      watchChanges: false,

      // Pro možný override musí být jako poslední.
      ...opts
    });

    // Registrace adaptérů
    // TODO #555: Zbytečná smyčka, když je de facto podporovaný jen jeden
    // volitelný s pevným názvem 'storage'.
    Object.entries(dsAdapterMap).forEach(([key, dsAdapter]) => {
      this.ds.registerAdapter(key, dsAdapter);
    });

    // TODO #409: Onakvé, ideálně by měly být dvě implementace Store pro perzistentní
    // a transient resourcy, které ale nemají nic společného. Nyní společný kód,
    // ale musí se obzvlášť v testech pečlivě hlídat, aby se nepoužil stejný store,
    // což je sice z hlediska typů správně, ale sémanticky špatně.

    // Registrace perzistentních datových zdrojů
    //
    // Pozor, u perzistentních zdrojů s relacemi nemusí po startu zařízení
    // fungovat lazy načtení relací! JSData zřejmě nepodporuje lazy donačtení,
    // ale pokud se někde na odkazované entity šáhlo vedlejším efektem, tak
    // relační kolekce nemusí být prázdná, takže to někdy funguje. Ale to je
    // velice neslušné na to spoléhat, takže se MUSÍ vždy explicitně všem metodám
    // vracející list/entitu dané parent entity s vazbami do `options.with` předat
    // explicitní seznam všech eager načítaných relací. Nebo ještě možná lépe
    // toto explicitně dát zde do všech resource specific typovaných wrapperů napevno.
    // - https://www.js-data.io/v2.9/docs/relations
    // - https://www.js-data.io/v2.9/docs/dslocalstorageadapter#findresourceconfig-id-options
    // Teoreticky je možné dotáhnout relace ex-post pomocí loadRelations, ale
    // to se nedoporučuje, jelikož se na to může zapomenout a pak šáhnout a
    // dostat domněle prázdné kolekce a typově to nejde pohlídat.
    // - https://www.js-data.io/v2.9/docs/dsloadrelations
    // Úplně nejlepší řešení je však na problémové vazby se úplně vykašlat
    // a explicitně eager si donačítat podle potřeby potřebná data. Takové řešení
    // bylo nakonec aplikováno v #389 pro analytiku, jelikož původně měla
    // AnalyticsGroup vazbu analyticsCounters na AnalyticsCounter. Jiné perzistentní
    // entity vazby nemají, takže problem solved.
    this.ds.defineResource(analyticsCounterResource);
    this.ds.defineResource(analyticsGroupResource);
    this.ds.defineResource(assetResource);
    this.ds.defineResource(sessionResource);

    // Registrace transient datových zdrojů
    this.ds.defineResource(contentTaskResource);
    this.ds.defineResource(layerResource);
    this.ds.defineResource(layoutResource);
    this.ds.defineResource(mediumResource);
    this.ds.defineResource(playlistResource);
    this.ds.defineResource(trackResource);

    // Registrace legacy datových zdrojů (pouze pro destroyAll)
    this.ds.defineResource(exceptionResource);
    this.ds.defineResource(powerTaskResource);
    this.ds.defineResource(taskResource);
    this.ds.defineResource(timerResource);
  }

  clear() {
    this.ds.clear();
  }

  // Perzistentní resourcy (nesmí mít synchronní cache only metody) -----------

  /**
   * @private
   * @template {ResourceName<PersistentMap>} R
   * @param {R} resourceName
   * @param {PersistentMap[R][0]} attrs
   * @param {DSConfiguration} [options]
   * @returns {Promise<PersistentMap[R][1]>}
   */
  create(resourceName, attrs, options) {
    return promise(this.ds.create(resourceName, attrs, options));
  }

  // TODO: Paralelně nebo sériově? Raději po zkušenostech pro jistotu sériově.
  // TODO: Možná refaktorovat na this.ds.createCollection? Tím pádem by to bylo
  // v pseudotransakci a otázku paralelizace by si řešil až adaptér.
  /**
   * @private
   * @template {ResourceName<PersistentMap>} R
   * @param {R} resourceName
   * @param {PersistentMap[R][0][]} attrsArray
   * @param {DSConfiguration} [options]
   * @returns {Promise<PersistentMap[R][1][]>}
   */
  createArray(resourceName, attrsArray, options) {
    return PromiseUtil.allSerial(
      attrsArray.map((attrs) => () => this.create(resourceName, attrs, options))
    );
  }

  /**
   * @param {AnalyticsCounterAttrs} analyticsCounterAttrs
   * @param {DSConfiguration} [options]
   * @returns {Promise<AnalyticsCounter>}
   */
  createAnalyticsCounter(analyticsCounterAttrs, options) {
    return this.create(
      analyticsCounterResource.name,
      analyticsCounterAttrs,
      options
    );
  }

  /**
   * @param {AnalyticsCounterAttrs[]} analyticsCounterAttrsArray
   * @param {DSConfiguration} [options]
   * @returns {Promise<AnalyticsCounter[]>}
   */
  createAnalyticsCounterArray(analyticsCounterAttrsArray, options) {
    return this.createArray(
      analyticsCounterResource.name,
      analyticsCounterAttrsArray,
      options
    );
  }

  /**
   * @param {AnalyticsGroupAttrs} analyticsGroupAttrs
   * @param {DSConfiguration} [options]
   * @returns {Promise<AnalyticsGroup>}
   */
  createAnalyticsGroup(analyticsGroupAttrs, options) {
    return this.create(
      analyticsGroupResource.name,
      analyticsGroupAttrs,
      options
    );
  }

  /**
   * @param {AnalyticsGroupAttrs[]} analyticsGroupAttrsArray
   * @param {DSConfiguration} [options]
   * @returns {Promise<AnalyticsGroup[]>}
   */
  createAnalyticsGroupArray(analyticsGroupAttrsArray, options) {
    return this.createArray(
      analyticsGroupResource.name,
      analyticsGroupAttrsArray,
      options
    );
  }

  /**
   * @deprecated Používat jen v testech.
   * @param {AnalyticsGroupVxAttrs[]} analyticsGroupAttrsArray
   * @param {DSConfiguration} [options]
   * @returns {Promise<AnalyticsGroup[]>}
   */
  createAnalyticsGroupVxArray(analyticsGroupAttrsArray, options) {
    return this.createArray(
      analyticsGroupResource.name,
      /** @type {AnalyticsGroupAttrs[]} */
      (analyticsGroupAttrsArray),
      options
    );
  }

  /**
   * @param {AssetAttrs} assetAttrs
   * @param {DSConfiguration} [options]
   * @returns {Promise<Asset>}
   */
  createAsset(assetAttrs, options) {
    return this.create(assetResource.name, assetAttrs, options);
  }

  /**
   * @param {AssetAttrs[]} assetAttrsArray
   * @param {DSConfiguration} [options]
   * @returns {Promise<Asset[]>}
   */
  createAssetArray(assetAttrsArray, options) {
    return this.createArray(assetResource.name, assetAttrsArray, options);
  }

  /**
   * @param {SessionAttrs} sessionAttrs
   * @param {DSConfiguration} [options]
   * @returns {Promise<Session>}
   */
  createSession(sessionAttrs, options) {
    return this.create(sessionResource.name, sessionAttrs, options);
  }

  /**
   * @private
   * @template {ResourceName<PersistentMap>} R
   * @param {R} resourceName
   * @param {PersistentMap[R][0]['id']} id
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<void>}
   */
  destroy(resourceName, id, options) {
    return promise(this.ds.destroy(resourceName, id, options));
  }

  // Není záměrně jako generická metoda private, protože se používá
  // i na "migraci smazáním" legacy věcí.
  /**
   * @template {ResourceName<PersistentMap> | 'exception' | 'task'} R
   * @param {R} resourceName
   * @param {DSFilterParams<any>} [params] TODO #555
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<void>}
   */
  destroyAll(resourceName, params, options) {
    return promise(this.ds.destroyAll(resourceName, params, options));
  }

  /**
   * @param {DSFilterParams<AnalyticsCounterAttrs>} [params]
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<void>}
   */
  destroyAllAnalyticsCounter(params, options) {
    return this.destroyAll(analyticsCounterResource.name, params, options);
  }

  /**
   * @param {DSFilterParams<AnalyticsGroupAttrs>} [params]
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<void>}
   */
  destroyAllAnalyticsGroup(params, options) {
    return this.destroyAll(analyticsGroupResource.name, params, options);
  }

  /**
   * @param {AnalyticsGroupAttrs['id']} id
   * @returns {Promise<void>}
   */
  destroyAnalyticsGroup(id) {
    return this.destroy(analyticsGroupResource.name, id);
  }

  /**
   * @param {AssetAttrs['id']} id
   * @returns {Promise<void>}
   */
  destroyAsset(id) {
    return this.destroy(assetResource.name, id);
  }

  /**
   * @param {DSFilterParams<AssetAttrs>} [params]
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<void>}
   */
  destroyAllAsset(params, options) {
    return this.destroyAll(assetResource.name, params, options);
  }

  /**
   * @private
   * @template {ResourceName<PersistentMap>} R
   * @param {R} resourceName
   * @param {PersistentMap[R][0]['id']} id
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<PersistentMap[R][1] | undefined>} Vrátí `undefined` při nenalezení.
   */
  find(resourceName, id, options) {
    return promise(this.ds.find(resourceName, id, options)).catch((error) => {
      // TODO: Neuvěřitelně neobecné a JSDataLocalStorage adaptér specific!
      // JSData totiž zřejmě nedělá přemapování adaptér specific chyb na obecné!
      // Je to hrůza, ale lepší mít centrálně na jednom místě.
      if (error.message === 'Not Found!') {
        return undefined;
      }
      throw error;
    });
  }

  /**
   * @private
   * @template {ResourceName<PersistentMap>} R
   * @param {R} resourceName
   * @param {DSFilterParams<PersistentMap[R][0]>} [params]
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<PersistentMap[R][1][]>}
   */
  findAll(resourceName, params, options) {
    return promise(this.ds.findAll(resourceName, params, options));
  }

  /**
   * @param {AnalyticsCounterAttrs['id']} id
   * @returns {Promise<AnalyticsCounter | undefined>}
   */
  findAnalyticsCounter(id) {
    return this.find(analyticsCounterResource.name, id);
  }

  /**
   * @param {DSFilterParams<AnalyticsCounterAttrs>} [params]
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<AnalyticsCounter[]>}
   */
  findAllAnalyticsCounter(params, options) {
    return this.findAll(analyticsCounterResource.name, params, options);
  }

  /**
   * @param {AnalyticsGroupAttrs['id']} id
   * @returns {Promise<AnalyticsGroup | undefined>}
   */
  findAnalyticsGroup(id) {
    return this.find(analyticsGroupResource.name, id);
  }

  /**
   * @param {DSFilterParams<AnalyticsGroupAttrs>} [params]
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<AnalyticsGroup[]>}
   */
  findAllAnalyticsGroup(params, options) {
    return this.findAll(analyticsGroupResource.name, params, options);
  }

  /**
   * @param {AssetAttrs['id']} id
   * @returns {Promise<Asset | undefined>}
   */
  findAsset(id) {
    return this.find(assetResource.name, id);
  }

  /**
   * @param {DSFilterParams<AssetAttrs>} [params]
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<Asset[]>}
   */
  findAllAsset(params, options) {
    return this.findAll(assetResource.name, params, options);
  }

  /**
   * @param {SessionAttrs['id']} id
   * @returns {Promise<Session | undefined>}
   */
  findSession(id) {
    return this.find(sessionResource.name, id);
  }

  /**
   * @private
   * @template {ResourceName<PersistentMap>} R
   * @param {R} resourceName
   * @param {PersistentMap[R][0]['id']} id
   * @returns {boolean}
   */
  hasChanges(resourceName, id) {
    return this.ds.hasChanges(resourceName, id);
  }

  /**
   * @param {AssetAttrs['id']} id
   * @returns {boolean}
   */
  hasChangesAsset(id) {
    return this.hasChanges(assetResource.name, id);
  }

  /**
   * @param {SessionAttrs['id']} id
   * @returns {boolean}
   */
  hasChangesSession(id) {
    return this.hasChanges(sessionResource.name, id);
  }

  /**
   * @private
   * @template {ResourceName<PersistentMap>} R
   * @param {R} resourceName
   * @param {PersistentMap[R][0]['id']} id
   * @param {UpdateAttrs<PersistentMap[R][0]>} attrs
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<PersistentMap[R][1]>}
   */
  update(resourceName, id, attrs, options) {
    // TODO #555: Případná kontrola, že když je přítomno id v attrs, že je stejné jako parametr id?
    return promise(this.ds.update(resourceName, id, attrs, options));
  }

  // TODO: Zatím @protected, protože nepoužito, ale má být jednotně @private.
  /**
   * @protected
   * @template {ResourceName<PersistentMap>} R
   * @param {R} resourceName
   * @param {UpdateAttrs<PersistentMap[R][0]>} attrs
   * @param {DSFilterParams<PersistentMap[R][0]>} [params]
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<PersistentMap[R][1][]>}
   */
  updateAll(resourceName, attrs, params, options) {
    return promise(this.ds.updateAll(resourceName, attrs, params, options));
  }

  /**
   * @param {SessionAttrs['id']} id
   * @param {UpdateAttrs<SessionAttrs>} sessionAttrs
   * @param {DSAdapterOperationConfiguration} [options]
   * @returns {Promise<Session>}
   */
  updateSession(id, sessionAttrs, options) {
    // TODO: Případná kontrola, že když je přítomno id v sessionAttrs, že je stejné jako parametr id?
    return this.update(sessionResource.name, id, sessionAttrs, options);
  }

  /**
   * @private
   * @template {ResourceName<PersistentMap>} R
   * @param {R} resourceName
   * @param {PersistentMap[R][0]['id']} id
   * @param {DSSaveConfiguration} [options]
   * @returns {Promise<PersistentMap[R][1]>}
   */
  save(resourceName, id, options) {
    return promise(this.ds.save(resourceName, id, options));
  }

  /**
   * @param {AnalyticsCounterAttrs['id']} id
   * @param {DSSaveConfiguration} [options]
   * @returns {Promise<AnalyticsCounter>}
   */
  saveAnalyticsCounter(id, options) {
    return this.save(analyticsCounterResource.name, id, options);
  }

  /**
   * @param {AssetAttrs['id']} id
   * @param {DSSaveConfiguration} [options]
   * @returns {Promise<Asset>}
   */
  saveAsset(id, options) {
    return this.save(assetResource.name, id, options);
  }

  /**
   * @param {SessionAttrs['id']} id
   * @param {DSSaveConfiguration} [options]
   * @returns {Promise<Session>}
   */
  saveSession(id, options) {
    return this.save(sessionResource.name, id, options);
  }

  // Transient resourcy (smí mít jen inject*/Array metody) --------------------

  /**
   * @private
   * @template {ResourceName<TransientMap>} R
   * @param {R} resourceName
   * @param {TransientMap[R][0]} attrs
   * @param {DSConfiguration} [options]
   * @returns {TransientMap[R][1]}
   */
  inject(resourceName, attrs, options) {
    return this.ds.inject(resourceName, attrs, options);
  }

  /**
   * @private
   * @template {ResourceName<TransientMap>} R
   * @param {R} resourceName
   * @param {TransientMap[R][0][]} attrsArray
   * @param {DSConfiguration} [options]
   * @returns {TransientMap[R][1][]}
   */
  injectArray(resourceName, attrsArray, options) {
    return this.ds.inject(resourceName, attrsArray, options);
  }

  /**
   * @param {ContentTaskAttrs} contentTaskAttrs
   * @param {DSConfiguration} [options]
   * @returns {ContentTask}
   */
  injectContentTask(contentTaskAttrs, options) {
    return this.inject(contentTaskResource.name, contentTaskAttrs, options);
  }

  /**
   * @param {ContentTaskAttrs[]} contentTaskAttrsArray
   * @param {DSConfiguration} [options]
   * @returns {ContentTask[]}
   */
  injectContentTaskArray(contentTaskAttrsArray, options) {
    return this.injectArray(
      contentTaskResource.name,
      contentTaskAttrsArray,
      options
    );
  }

  /**
   * @param {LayerAttrs} layerAttrs
   * @param {DSConfiguration} [options]
   * @returns {Layer}
   */
  injectLayer(layerAttrs, options) {
    return this.inject(layerResource.name, layerAttrs, options);
  }

  /**
   * @param {LayerAttrs[]} layerAttrsArray
   * @param {DSConfiguration} [options]
   * @returns {Layer[]}
   */
  injectLayerArray(layerAttrsArray, options) {
    return this.injectArray(layerResource.name, layerAttrsArray, options);
  }

  /**
   * @param {LayoutAttrs} layoutAttrs
   * @param {DSConfiguration} [options]
   * @returns {Layout}
   */
  injectLayout(layoutAttrs, options) {
    return this.inject(layoutResource.name, layoutAttrs, options);
  }

  /**
   * @param {LayoutAttrs[]} layoutAttrsArray
   * @param {DSConfiguration} [options]
   * @returns {Layout[]}
   */
  injectLayoutArray(layoutAttrsArray, options) {
    return this.injectArray(layoutResource.name, layoutAttrsArray, options);
  }

  /**
   * @param {MediumAttrs} mediumAttrs
   * @param {DSConfiguration} [options]
   * @returns {Medium}
   */
  injectMedium(mediumAttrs, options) {
    return this.inject(mediumResource.name, mediumAttrs, options);
  }

  /**
   * @param {MediumAttrs[]} mediumAttrsArray
   * @param {DSConfiguration} [options]
   * @returns {Medium[]}
   */
  injectMediumArray(mediumAttrsArray, options) {
    return this.injectArray(mediumResource.name, mediumAttrsArray, options);
  }

  /**
   * @param {PlaylistAttrs} playlistAttrs
   * @param {DSConfiguration} [options]
   * @returns {Playlist}
   */
  injectPlaylist(playlistAttrs, options) {
    return this.inject(playlistResource.name, playlistAttrs, options);
  }

  /**
   * @param {PlaylistAttrs[]} playlistAttrsArray
   * @param {DSConfiguration} [options]
   * @returns {Playlist[]}
   */
  injectPlaylistArray(playlistAttrsArray, options) {
    return this.injectArray(playlistResource.name, playlistAttrsArray, options);
  }

  /**
   * @param {TrackAttrs} trackAttrs
   * @param {DSConfiguration} [options]
   * @returns {Track}
   */
  injectTrack(trackAttrs, options) {
    return this.inject(trackResource.name, trackAttrs, options);
  }

  /**
   * @param {TrackAttrs[]} trackAttrsArray
   * @param {DSConfiguration} [options]
   * @returns {Track[]}
   */
  injectTrackArray(trackAttrsArray, options) {
    return this.injectArray(trackResource.name, trackAttrsArray, options);
  }
}

export default Store;

/**
 * Cast from JSDataPromise to standard Promise type.
 * @example
 * return promise(store.find('foo', 42))
 *   .then((foo) => foo.count)
 *   .then((count) => count * count);
 * @template T
 * @param {import('js-data').JSDataPromise<T>} p JSData Promise.
 * @returns {Promise<T>} Standard Promise.
 */
function promise(p) {
  return /** @type {Promise<T>} */ (p);
}

// TODO: Lepší umístění/pojmenování, je to něco používané jen buildery a jen v testech.
/**
 * Utility typ pro model buildery používané v testech.
 * @template T
 * @typedef {T & { store: Store }} AttrsWithStore
 */
